import type { MenuItem } from '@frontend/libs/types/site-configuration'
import type React from 'react'

import { cn } from '@nextui-org/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import AsavaMegaMenu from './AsavaMegaMenu'
import AsvMegaMenu from './AsvMegaMenu'
import CollaborationMegaMenu from './CollaborationMegaMenu'
import GroupMegaMenu from './GroupMegaMenu'
import ProjectMegaMenu from './ProjectMegaMenu'

const MegaMenuComponents = {
  'mega-menu.asava': AsavaMegaMenu,
  'mega-menu.asv': AsvMegaMenu,
  'mega-menu.collaboration': CollaborationMegaMenu,
  'mega-menu.group': GroupMegaMenu,
  'mega-menu.projects': ProjectMegaMenu,
}

const MegaMenu: React.FC<{ menu: MenuItem }> = ({ menu }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const menuElement = useRef(null) // Add this line

  const handleMouseLeave = useCallback(() => setIsOpen(false), [])
  const handleMouseOver = useCallback(() => setIsOpen(true), [])

  const MegaMenuComponent = MegaMenuComponents[menu.description]

  const location = useLocation()

  useEffect(() => {
    const currentMenuElement = menuElement.current

    if (currentMenuElement) {
      currentMenuElement.addEventListener('mouseover', handleMouseOver)
      currentMenuElement.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      setIsOpen(false)
      if (currentMenuElement) {
        currentMenuElement.removeEventListener('mouseover', handleMouseOver)
        currentMenuElement.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [location, handleMouseOver, handleMouseLeave])

  return (
    <li
      className={cn('menu-item font-medium uppercase', { active: isOpen })}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
      ref={menuElement}
    >
      <Link color="primary" title={menu.title} to={menu.url}>
        {menu.title}
      </Link>
      <div
        className={cn(
          'absolute left-0 z-0 block h-0 w-full translate-y-1 transform bg-white py-8 font-normal opacity-0 shadow-lg transition-opacity duration-200 ease-in-out',
          {
            invisible: !isOpen,
            'visible z-20 h-auto translate-y-0 opacity-100': isOpen,
          },
        )}
      >
        {MegaMenuComponent && <MegaMenuComponent />}
      </div>
    </li>
  )
}

export default MegaMenu
